import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, Clock3, Link2, ListChecks, RefreshCw, Siren, UsersRound } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { AttendancePanel } from "@/components/connecteam/AttendancePanel";
import { RosterReconciliationPanel } from "@/components/connecteam/RosterReconciliationPanel";
import { UnscheduledAssignmentsPanel } from "@/components/connecteam/UnscheduledAssignmentsPanel";
import { SyncStatusPanel } from "@/components/connecteam/SyncStatusPanel";
import { TimeExceptionsPanel } from "@/components/connecteam/TimeExceptionsPanel";
import { UserMappingPanel } from "@/components/connecteam/UserMappingPanel";
import { WeeklyHoursPanel } from "@/components/connecteam/WeeklyHoursPanel";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const HOURS_ROLES = ["hr", "system_administrator"];

const tabs = [
  { key: "status", label: "Sync status", icon: RefreshCw },
  { key: "mapping", label: "User mapping", icon: Link2 },
  { key: "roster", label: "Roster reconciliation", icon: UsersRound },
  { key: "exceptions", label: "Time exceptions", icon: ListChecks, hours: true },
  { key: "hours", label: "Weekly hours", icon: CalendarClock, hours: true },
  { key: "attendance", label: "Attendance", icon: Siren },
];

export default function ConnecteamPage() {
  const { user } = useAuth();
  const canSeeHours = HOURS_ROLES.includes(user?.role);
  const canSync = user?.role === "system_administrator";
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (tabs.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "status"));
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [syncing, setSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setStatus((await api.get("/connecteam/status")).data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function runSync() {
    setSyncing(true);
    setError("");
    setNotice("");
    try {
      const run = (await api.post("/connecteam/sync")).data;
      const c = run.counts || {};
      setNotice(run.status === "success" ? `Sync complete: ${c.users ?? 0} users, ${c.jobs ?? 0} sites, ${c.shifts ?? 0} shifts, ${c.time_entries ?? 0} time entries, ${c.open_exceptions ?? 0} open exceptions.` : `Sync finished with errors (${(run.errors || []).length}). The System Administrator has been alerted.`);
      await loadStatus();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSyncing(false);
    }
  }

  const visibleTabs = tabs.filter((item) => !item.hours || canSeeHours);
  const totals = status?.totals || {};

  return (
    <div className="app-shell" data-testid="connecteam-shell">
      <AppSidebar activeItem="Connecteam" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page connecteam-page" data-testid="connecteam-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">OPERATIONS · READ-ONLY INTEGRATION</p>
              <h1>Connecteam</h1>
              <p className="page-subtitle">TAGForce reads users, scheduled shifts and time clock entries from Connecteam (a full sync at 6:00 a.m. ET, then today's shifts and clock-ins every 15 minutes on weekdays) and never writes back. Corrections are made in Connecteam.</p>
            </div>
            <div className="page-intro-actions">
              <span className="pairing-meta" data-testid="connecteam-last-sync">{!status ? "Loading sync status…" : status.last_success_at ? `Last successful sync ${new Date(status.last_success_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : "No successful sync yet"}</span>
              {canSync && <button className="button button-primary" data-testid="connecteam-sync-button" disabled={syncing} type="button" onClick={runSync}><RefreshCw size={14} className={syncing ? "spin" : ""} /> {syncing ? "Syncing…" : "Sync now"}</button>}
            </div>
          </section>
          {error && <p className="form-message error-message" data-testid="connecteam-error">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="connecteam-notice">{notice}</p>}
          <section className="provider-summary-grid connecteam-summary" data-testid="connecteam-summary">
            <button type="button" onClick={() => setTab("mapping")}><UsersRound size={18} /><strong data-testid="summary-active-users">{totals.active_users ?? "—"}</strong><span>{status ? `active Connecteam users · ${totals.unmatched_active_users ?? 0} unmatched` : "loading Connecteam users…"}</span></button>
            <button type="button" onClick={() => setTab("status")}><Clock3 size={18} /><strong data-testid="summary-time-entries">{totals.time_entries ?? "—"}</strong><span>{status ? `time entries · ${totals.shifts ?? 0} scheduled shifts (last ${status.window_days ?? 42} days)` : "loading time entries…"}</span></button>
            <button type="button" onClick={() => canSeeHours && setTab("exceptions")}><ListChecks size={18} /><strong data-testid="summary-open-exceptions">{canSeeHours ? totals.open_exceptions ?? "—" : "—"}</strong><span>{canSeeHours ? "open time exceptions" : "time exceptions (HR / System Administrator)"}</span></button>
          </section>
          <nav className="data-admin-tabs connecteam-tabs" data-testid="connecteam-tabs">
            {visibleTabs.map((item) => {
              const Icon = item.icon;
              return <button key={item.key} className={tab === item.key ? "active" : ""} data-testid={`connecteam-tab-${item.key}`} type="button" onClick={() => setTab(item.key)}><Icon size={14} /> {item.label}</button>;
            })}
          </nav>
          {tab === "status" && <SyncStatusPanel status={status} />}
          {tab === "mapping" && <UserMappingPanel canMap={["operations", "hr", "system_administrator"].includes(user?.role)} onChanged={loadStatus} />}
          {tab === "roster" && <><RosterReconciliationPanel />{canSeeHours && <UnscheduledAssignmentsPanel />}</>}
          {tab === "exceptions" && canSeeHours && <TimeExceptionsPanel onChanged={loadStatus} canMap={["operations", "hr", "system_administrator"].includes(user?.role)} user={user} initialType={searchParams.get("type") || "all"} />}
          {tab === "hours" && canSeeHours && <WeeklyHoursPanel />}
          {tab === "attendance" && <AttendancePanel canRun={canSync} />}
        </main>
      </div>
    </div>
  );
}
